/**
 * Operator HTML in the app (MOB-121, ADR 0011, doctrine E55).
 *
 * Each `v-html` site shows operator copy only through safeHtml (the global property `$safeHtml`),
 * and each binding that takes an operator URL goes through safeUrl (`$safeUrl`). The policy itself
 * (what is removed, the CSS allow-list, the URL schemes) is the layer module HtmlPolicy, which the
 * e-mails use too.
 */
import DOMPurify from 'dompurify';
import HtmlPolicy from '../../amplify/backend/function/matesonlinebookingMobLibrary/opt/HtmlPolicy.js';

let policy = null;

function getPolicy() {
  if (policy === null) {
    policy = HtmlPolicy.configure(DOMPurify);
  }
  return policy;
}

/**
 * Operator HTML with its active content removed and its formatting kept.
 *
 * @param {*} html - null and undefined give an empty string
 * @param {{inline?: boolean}} [options] - inline gives the inline form (MOB-461): the block
 *   elements are unwrapped with 1 space between their contents, `<br>` becomes a space, and the
 *   inline tags stay. Use it where the title sits in a line of text. The global property is
 *   `$safeHtml(x, { inline: true })`.
 * @returns {string}
 */
export function safeHtml(html, options) {
  return getPolicy().sanitize(html, options && options.inline ? { inline: true } : undefined);
}

/**
 * The text of operator HTML: all elements removed, all entities decoded (MOB-461, doctrine E87).
 * For a place that cannot show HTML: an attribute (`alt`, `aria-label`, `title`), a dropdown item,
 * the document title, a breadcrumb. The result is text: a text interpolation shows it as it is.
 *
 * @param {*} html - null and undefined give an empty string
 * @param {{lineBreaks?: boolean}} [options] - lineBreaks turns a block element and `<br>` into a
 *   newline and keeps the newlines. Else they become 1 space.
 * @returns {string}
 */
export function plainText(html, options) {
  return getPolicy().plainText(html, options);
}

/**
 * An operator URL for an `href` or `src` binding, or undefined when its scheme is not allowed
 * (`javascript:`, `data:`, …). Vue renders no attribute for undefined.
 *
 * @param {*} url
 * @returns {string|undefined}
 */
export function safeUrl(url) {
  const safe = HtmlPolicy.safeUrl(url);
  return safe === null ? undefined : safe;
}

/**
 * Operator HTML with its active content removed, and the target of each link set:
 * - a link on the current domain: target='_self'
 * - a link to another domain: target='_blank' with rel='noopener noreferrer'
 *
 * The parse is inert (DOMPurify), so no image of the copy loads and no handler runs before the
 * copy renders (map decision 13).
 *
 * @param {string} htmlString - The HTML string to process
 * @param {string} currentDomain - Optional current domain (defaults to window.location.hostname)
 * @returns {string} The sanitised HTML with the link targets set; a non-string comes back unchanged
 */
export function processAnchorTargets(htmlString, currentDomain = null) {
  if (!htmlString || typeof htmlString !== 'string') {
    return htmlString;
  }
  const domain = currentDomain || (typeof window !== 'undefined' ? window.location.hostname : '');
  return getPolicy().sanitize(htmlString, { linkTargets: true, domain });
}
